import { useCallback, useEffect, useState } from 'react';
import { supabase, isPlaceholderSupabase } from '../supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { TtsProvider } from '../../hooks/useTextToSpeech';

export interface VoicePrefs {
  voice_enabled: boolean;
  voice_provider: TtsProvider;
  voice_rate: number;
  voice_id: string | null;
}

/** Recommended default — cloud TTS with OpenAI's "fable" voice (warm,
 *  British-leaning male). Picked for English coaching use. */
const DEFAULTS: VoicePrefs = {
  voice_enabled: true,
  voice_provider: 'cloud',
  voice_rate: 1.0,
  voice_id: 'fable',
};

/** Legacy default that shipped before the cloud + fable recommendation —
 *  used by the one-time migration below to detect "user never picked,
 *  just got the old default" cases. */
const LEGACY_DEFAULT_SHAPE = { voice_provider: 'system', voice_id: null } as const;

const STORAGE_KEY = '4cm_voice_prefs';

interface Result {
  prefs: VoicePrefs;
  loading: boolean;
  save: (next: Partial<VoicePrefs>) => Promise<void>;
}

function readCachedPrefs(): VoicePrefs | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<VoicePrefs>;
    // One-time migration — if cached prefs match the legacy default
    // exactly, replace with the new recommended default. Preserves any
    // explicit user pick.
    if (
      parsed.voice_provider === LEGACY_DEFAULT_SHAPE.voice_provider &&
      (parsed.voice_id === LEGACY_DEFAULT_SHAPE.voice_id || parsed.voice_id == null)
    ) {
      writeCachedPrefs(DEFAULTS);
      return DEFAULTS;
    }
    return { ...DEFAULTS, ...parsed };
  } catch {
    return null;
  }
}

function writeCachedPrefs(prefs: VoicePrefs): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota / privacy mode errors */
  }
}

// ---------------------------------------------------------------------------
// Module-scoped subscription store — keeps every useVoicePrefs() consumer in
// sync within the same tab. Without this, a save() in the VoicePicker would
// update the picker's component state + localStorage, but Onboarding's
// separate useVoicePrefs() instance wouldn't see the change until a remount.
// Bug 2026-05-20: voice picker showed the new pick, but Brain's TTS kept
// using the previous voice — Onboarding's prefs state was stale.
// ---------------------------------------------------------------------------

type Listener = (prefs: VoicePrefs) => void;
const listeners = new Set<Listener>();

function publish(prefs: VoicePrefs): void {
  for (const fn of listeners) {
    try { fn(prefs); } catch { /* ignore listener errors */ }
  }
}

function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Read + write the active coach's TTS preferences (migrations 00067/00068
 * shipped these as columns on `users`). Caches to localStorage for offline
 * mode + instant first-render. Save updates BOTH the live row and the
 * cache so subsequent loads stay consistent.
 *
 * Multiple component instances of this hook stay in sync via a module-
 * scoped subscription — when one component saves, every other instance
 * re-renders with the new prefs immediately.
 */
export function useVoicePrefs(): Result {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<VoicePrefs>(() => readCachedPrefs() ?? DEFAULTS);
  const [loading, setLoading] = useState(false);

  // Sync with other instances within the same tab. Also catches storage
  // events from OTHER tabs so opening the picker in tab A while Brain
  // is speaking in tab B works correctly.
  useEffect(() => {
    const unsubscribe = subscribe((next) => setPrefs(next));
    const onStorage = (e: StorageEvent): void => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        const parsed = JSON.parse(e.newValue) as Partial<VoicePrefs>;
        setPrefs({ ...DEFAULTS, ...parsed });
      } catch { /* ignore */ }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
    }
    return () => {
      unsubscribe();
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage);
      }
    };
  }, []);

  // Hydrate from the DB whenever the user changes.
  useEffect(() => {
    if (!user) return;
    if (isPlaceholderSupabase()) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data } = await supabase
        .from('users')
        .select('voice_enabled, voice_provider, voice_rate, voice_id')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      setLoading(false);
      if (!data) return;
      const rawProvider = data.voice_provider;
      const provider: TtsProvider =
        rawProvider === 'eleven' ? 'eleven'
        : rawProvider === 'cloud' ? 'cloud'
        : 'system';
      const next: VoicePrefs = {
        voice_enabled: data.voice_enabled ?? DEFAULTS.voice_enabled,
        voice_provider: provider,
        voice_rate: typeof data.voice_rate === 'number' ? data.voice_rate : DEFAULTS.voice_rate,
        voice_id: data.voice_id ?? null,
      };
      setPrefs(next);
      writeCachedPrefs(next);
      publish(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const save = useCallback(async (patch: Partial<VoicePrefs>) => {
    setPrefs((prev) => {
      const next: VoicePrefs = { ...prev, ...patch };
      writeCachedPrefs(next);
      // Broadcast to every other useVoicePrefs() instance in this tab.
      publish(next);
      // Best-effort live persist when authenticated + online. Local
      // already updated; failure here doesn't roll back state.
      if (user && !isPlaceholderSupabase()) {
        void supabase
          .from('users')
          .update({
            voice_enabled: next.voice_enabled,
            voice_provider: next.voice_provider,
            voice_rate: next.voice_rate,
            voice_id: next.voice_id,
          })
          .eq('id', user.id);
      }
      return next;
    });
  }, [user]);

  return { prefs, loading, save };
}
